import React, { useState, useEffect } from "react";
import "./Profile.css";
import LegalQuestionnaire from "../LegalQuestionnaire/LegalQuestionnaire";
import { useNavigate } from "react-router-dom";
import base_url from "../api";

const Profile = () => {
  const [activeTab, setActiveTab] = useState("profile");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  // Password update state
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  // Profile update state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    houseNumber: "",
    street: "",
    city: "",
    country: "",
    postalCode: "",
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [profileSuccess, setProfileSuccess] = useState("");

  useEffect(() => {
    fetchUserInfo();
    
    // Listen for switch to profile tab event
    const handleSwitchToProfile = () => {
      setActiveTab("profile");
    };
    
    window.addEventListener('switchToProfileTab', handleSwitchToProfile);
    
    return () => {
      window.removeEventListener('switchToProfileTab', handleSwitchToProfile);
    };
  }, []);

  const fetchUserInfo = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(`${base_url}/me`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          // Token invalid, clear storage and redirect to login
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          navigate("/login");
          return;
        }
        throw new Error(data.message || "Failed to fetch user info");
      }

      setUser(data.user);
      // Initialize profile data
      setProfileData({
        firstName: data.user.firstName || "",
        lastName: data.user.lastName || "",
        email: data.user.email || "",
        phone: data.user.phone || "",
        houseNumber: data.user.house_number || "",
        street: data.user.street || "",
        city: data.user.city || "",
        country: data.user.country || "",
        postalCode: data.user.postal_code || "",
      });
    } catch (error) {
      console.error("Error fetching user info:", error);
      // If error, try to use stored user data as fallback
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setProfileData({
          firstName: parsedUser.firstName || "",
          lastName: parsedUser.lastName || "",
          email: parsedUser.email || "",
          phone: parsedUser.phone || "",
          houseNumber: parsedUser.house_number || "",
          street: parsedUser.street || "",
          city: parsedUser.city || "",
          country: parsedUser.country || "",
          postalCode: parsedUser.postal_code || "",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({
      ...passwordData,
      [name]: value,
    });
    // Clear errors when user types
    if (passwordError) setPasswordError("");
    if (passwordSuccess) setPasswordSuccess("");
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData({
      ...profileData,
      [name]: value,
    });
    // Clear errors when user types
    if (profileError) setProfileError("");
    if (profileSuccess) setProfileSuccess("");
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setProfileError("");
    setProfileSuccess("");

    setProfileLoading(true);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(`${base_url}/update-profile`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(profileData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to update profile");
      }

      setProfileSuccess("Profile updated successfully!");
      setUser(data.user);
      setIsEditingProfile(false);
      // Update stored user data
      localStorage.setItem("user", JSON.stringify(data.user));
    } catch (error) {
      setProfileError(error.message || "Failed to update profile. Please try again.");
    } finally {
      setProfileLoading(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");
    
    // Client-side validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError("Please fill in all password fields");
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError("New password must be at least 6 characters long");
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError("New password and confirm password do not match");
      return;
    }

    if (passwordData.newPassword === passwordData.currentPassword) {
      setPasswordError("New password must be different from your current password");
      return;
    }

    setPasswordLoading(true);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(`${base_url}/update-password`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(passwordData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to update password");
      }

      setPasswordSuccess("Password updated successfully!");
      // Clear form
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      setPasswordError(error.message || "Failed to update password. Please try again.");
    } finally {
      setPasswordLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="profile-page">
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p>Unable to load user information. Please try logging in again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      {/* Header */}
      <div className="profile-header">
        <h1>Welcome back, {user.firstName}!</h1>
        <p>Manage your profile and legal questionnaires</p>

        <div className="profile-tabs">
          <button
            className={activeTab === "profile" ? "active" : ""}
            onClick={() => setActiveTab("profile")}
          >
            Profile Settings
          </button>

          <button
            className={activeTab === "legal" ? "active" : ""}
            onClick={() => setActiveTab("legal")}
          >
            Legal Questionnaires
          </button>
        </div>
      </div>

      {/* ================= CONTENT ================= */}

      {/* PROFILE TAB */}
      {activeTab === "profile" && (
        <div className="profile-content">
          {/* Account Details */}
          <div className="card">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <div>
                <h3>Account Details</h3>
                <p className="subtitle">Your personal information</p>
              </div>
              {!isEditingProfile && (
                <button
                  className="secondary-btn"
                  onClick={() => setIsEditingProfile(true)}
                >
                  <i className="fa-solid fa-edit"></i> Edit Profile
                </button>
              )}
            </div>

            {!isEditingProfile ? (
              <>
                <div className="detail">
                  <span>First Name</span>
                  <strong>{user.firstName || "Not provided"}</strong>
                </div>

                <div className="detail">
                  <span>Last Name</span>
                  <strong>{user.lastName || "Not provided"}</strong>
                </div>

                <div className="detail">
                  <span>Email Address</span>
                  <strong>{user.email || "Not provided"}</strong>
                </div>

                <div className="detail">
                  <span>Phone Number</span>
                  <strong>{user.phone || "Not provided"}</strong>
                </div>

                <div style={{ marginTop: "20px", marginBottom: "10px", paddingTop: "20px", borderTop: "1px solid #e5e7eb" }}>
                  <h4 style={{ marginBottom: "15px", color: "#1e3a8a", fontSize: "16px", fontWeight: "600" }}>Address Information</h4>
                </div>

                <div className="detail">
                  <span>House Number</span>
                  <strong>{user.house_number || "Not provided"}</strong>
                </div>

                <div className="detail">
                  <span>Street Name</span>
                  <strong>{user.street || "Not provided"}</strong>
                </div>

                <div className="detail">
                  <span>City</span>
                  <strong>{user.city || "Not provided"}</strong>
                </div>

                <div className="detail">
                  <span>Country</span>
                  <strong>{user.country || "Not provided"}</strong>
                </div>

                <div className="detail">
                  <span>Postal Code</span>
                  <strong>{user.postal_code || "Not provided"}</strong>
                </div>

                <div style={{ marginTop: "20px", marginBottom: "10px", paddingTop: "20px", borderTop: "1px solid #e5e7eb" }}>
                  <h4 style={{ marginBottom: "15px", color: "#1e3a8a", fontSize: "16px", fontWeight: "600" }}>Account Information</h4>
                </div>

                <div className="detail">
                  <span>Member Since</span>
                  <strong>{formatDate(user.createdAt)}</strong>
                </div>
              </>
            ) : (
              <form onSubmit={handleUpdateProfile}>
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    name="firstName"
                    placeholder="Enter first name"
                    value={profileData.firstName}
                    onChange={handleProfileChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Enter last name"
                    value={profileData.lastName}
                    onChange={handleProfileChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter email address"
                    value={profileData.email}
                    onChange={handleProfileChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Enter phone number"
                    value={profileData.phone}
                    onChange={handleProfileChange}
                    required
                  />
                </div>

                <div style={{ marginTop: "20px", marginBottom: "10px", paddingTop: "20px", borderTop: "1px solid #e5e7eb" }}>
                  <h4 style={{ marginBottom: "15px", color: "#1e3a8a", fontSize: "16px" }}>Address Information</h4>
                </div>

                <div className="form-group">
                  <label>House Number *</label>
                  <input
                    type="text"
                    name="houseNumber"
                    placeholder="Enter house/building number"
                    value={profileData.houseNumber}
                    onChange={handleProfileChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Street Name *</label>
                  <input
                    type="text"
                    name="street"
                    placeholder="Enter street name"
                    value={profileData.street}
                    onChange={handleProfileChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    name="city"
                    placeholder="Enter city"
                    value={profileData.city}
                    onChange={handleProfileChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Country *</label>
                  <input
                    type="text"
                    name="country"
                    placeholder="Enter country"
                    value={profileData.country}
                    onChange={handleProfileChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Postal Code *</label>
                  <input
                    type="text"
                    name="postalCode"
                    placeholder="Enter postal code"
                    value={profileData.postalCode}
                    onChange={handleProfileChange}
                    required
                  />
                </div>

                {profileError && (
                  <p style={{ color: "red", fontSize: "14px", marginBottom: "12px" }}>
                    {profileError}
                  </p>
                )}

                {profileSuccess && (
                  <p style={{ color: "green", fontSize: "14px", marginBottom: "12px" }}>
                    {profileSuccess}
                  </p>
                )}

                <div style={{ display: "flex", gap: "12px" }}>
                  <button
                    type="submit"
                    className="primary-btn"
                    disabled={profileLoading}
                  >
                    {profileLoading ? "Updating..." : "Save Changes"}
                  </button>
                  <button
                    type="button"
                    className="secondary-btn"
                    onClick={() => {
                      setIsEditingProfile(false);
                      setProfileError("");
                      setProfileSuccess("");
                      // Reset to current user data
                      setProfileData({
                        firstName: user.firstName || "",
                        lastName: user.lastName || "",
                        email: user.email || "",
                        phone: user.phone || "",
                        houseNumber: user.house_number || "",
                        street: user.street || "",
                        city: user.city || "",
                        country: user.country || "",
                        postalCode: user.postal_code || "",
                      });
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Update Password */}
          <div className="card">
            <h3>Update Password</h3>
            <p className="subtitle">Change your account password</p>

            <form onSubmit={handleUpdatePassword}>
              <div className="form-group">
                <label>Current Password</label>
                <input
                  type="password"
                  name="currentPassword"
                  placeholder="Enter current password"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  name="newPassword"
                  placeholder="Enter new password"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength={6}
                />
              </div>

              <div className="form-group">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  name="confirmPassword"
                  placeholder="Confirm new password"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                  minLength={6}
                />
              </div>

              {passwordError && (
                <p style={{ color: "red", fontSize: "14px", marginBottom: "12px" }}>
                  {passwordError}
                </p>
              )}

              {passwordSuccess && (
                <p style={{ color: "green", fontSize: "14px", marginBottom: "12px" }}>
                  {passwordSuccess}
                </p>
              )}

              <button
                type="submit"
                className="primary-btn"
                disabled={passwordLoading}
              >
                {passwordLoading ? "Updating..." : "Update Password"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* LEGAL TAB */}
      {activeTab === "legal" && (
        <div className="legal-content">
          <LegalQuestionnaire />
        </div>
      )}
    </div>
  );
};

export default Profile;
